import PropTypes from "prop-types";
import "./styles.css";
import TextInput from "../../Inputs/TextInput";
import Select from "../../Inputs/Select";
import {
  PropertyTypeOptions,
  PropertyBedOptions,
  PropertyFreeholdLeaseholdOptions,
  OwnerOccupationOptions,
  FloorAreaOptions,
  YearBuiltOptions,
  CouncilTaxOptions,
  EpcOptions,
  PrimaryHeatingOptions,
} from "../../../Leadform/options";
import { useLeadForm } from "../../../context/leadform";
import { useEffect, useState } from "react";
import { capitaliseFirstLetter, stripNonAlphanumeric } from "../../../helpers";

function InfoStep({}) {
  const { responses, setResponses, updateValue, sessionKey } = useLeadForm();
  const [propertyInfo, setPropertyInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);
        let address = responses.address;
        if (address === "" || address === undefined) {
          const addressParts = [
            responses.line1,
            responses.line2,
            responses.town,
            responses.county,
            responses.postcode,
          ];
          address = addressParts
            .filter((part) => part && part.trim() !== "")
            .join(", ");
          updateValue("address", address);
        }
        const info = await getInfo(address);

        if (info.exact_match !== true) {
          console.log("Caught status code");
          setFetched(false);
          setLoading(false);
          throw new Error("Failed to get info");
        }

        setPropertyInfo(info);

        setResponses((prev) => ({
          ...prev,
          country: info.plus.property.attributes.status.country.toLowerCase(),
          postcode: info.plus.property.attributes.status.postcode,
          property_type:
            info.plus.property.attributes.status.property_type?.toLowerCase() ??
            "",
          bedrooms: String(
            info.property.attributes.indoor.bedrooms_declared_and_predicted ??
              "",
          ),
          bathrooms: String(
            info.property.attributes.indoor.bathrooms_declared_and_predicted ??
              "",
          ),
          reception_rooms: String(
            info.property.attributes.indoor.reception_rooms ?? "",
          ),
          floor_area_sqm: String(
            info.property.attributes.indoor.floor_area_declared_and_predicted ??
              "",
          ),
          year_built: String(
            info.property.attributes.indoor.year_built_estimate ?? "",
          ),
          freehold_leasehold: stripNonAlphanumeric(
            info.property.ownership.lease_type ?? "",
          ),
          estimated_value: String(
            info.property.value.sale.property_value ?? "",
          ),
          council_tax_band: String(
            info.property.bills.tax.council_tax_band_declared_and_predicted ??
              "",
          ),
          epc_rating: String(
            info.property.bills.energy
              .current_energy_rating_declared_and_predicted ?? "",
          ),
          primary_heating: String(
            info.property.bills.energy.primary_heating_source ?? "",
          ),
        }));
        setFetched(true);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, []);

  const getInfo = async (
    address: string | number | readonly string[],
  ): Promise<string | any> => {
    try {
      const response = await fetch(
        `/wp-json/wordpress-leadform/v1/info?address=${address}&session=${sessionKey}`,
      );
      const data = await response.json();
      return data;
    } catch (error) {
      return error;
    }
  };

  return (
    <div className="Step InfoStep">
      {loading ? (
        <h2>Please wait whilst we check your property details...</h2>
      ) : !loading && fetched ? (
        <>
          <h2>We've found some details about your property</h2>
          <p>
            We've filled in these details automatically. Please check they're
            correct before continuing.
          </p>
          {(() => {
            const propertyTypeOptions = [
              {
                value: String(
                  propertyInfo.plus.property.attributes.status.property_type,
                ).toLowerCase(),
                label: String(
                  propertyInfo.plus.property.attributes.status.property_type,
                ),
              },
              ...PropertyTypeOptions,
            ];

            const floorAreaOptions = [
              String(
                propertyInfo.property.attributes.indoor
                  .floor_area_declared_and_predicted,
              ),
              ...FloorAreaOptions,
            ];

            let primaryHeatingOptions;
            if (
              propertyInfo.property.bills.energy.primary_heating_source !== null
            ) {
              primaryHeatingOptions = [
                String(
                  propertyInfo.property.bills.energy.primary_heating_source,
                ),
                ...PrimaryHeatingOptions,
              ];
            } else {
              primaryHeatingOptions = PrimaryHeatingOptions;
            }

            return (
              <div className="PropertySummary">
                <div className="InputGroup">
                  <Select
                    inline
                    label="Property Type"
                    options={propertyTypeOptions}
                    statekey="property_type"
                  />
                </div>

                <div className="InputGroup">
                  <Select
                    inline
                    label="Bedrooms"
                    options={PropertyBedOptions}
                    statekey="bedrooms"
                  />
                </div>

                <div className="InputGroup">
                  <Select
                    inline
                    label="Bathrooms"
                    options={PropertyBedOptions}
                    statekey="bathrooms"
                  />
                </div>
                <div className="InputGroup">
                  <Select
                    inline
                    label="Reception Rooms"
                    options={PropertyBedOptions}
                    statekey="reception_rooms"
                  />
                </div>
                <div className="InputGroup">
                  <Select
                    inline
                    label="Floor Area (sqm)"
                    options={floorAreaOptions}
                    statekey="floor_area_sqm"
                  />
                </div>
                <div className="InputGroup">
                  <Select
                    inline
                    label="Year Built"
                    options={YearBuiltOptions}
                    statekey="year_built"
                  />
                </div>
                <div className="InputGroup">
                  <Select
                    inline
                    label="Property Tenure"
                    options={PropertyFreeholdLeaseholdOptions}
                    statekey="freehold_leasehold"
                  />
                </div>
                <div className="InputGroup">
                  <Select
                    inline
                    label="Council Tax Band"
                    options={CouncilTaxOptions}
                    statekey="council_tax_band"
                  />
                </div>
                <div className="InputGroup">
                  <Select
                    inline
                    label="EPC Rating"
                    options={EpcOptions}
                    statekey="epc_rating"
                  />
                </div>
                <div className="InputGroup">
                  <Select
                    inline
                    label="Primary Heating Source"
                    options={primaryHeatingOptions}
                    statekey="primary_heating"
                  />
                </div>
              </div>
            );
          })()}
        </>
      ) : (
        <>
          <h2>Tell us more about your property</h2>
          <p>This will help to match you with a cash house buyer.</p>

          <div className="PropertySummary">
            <div className="InputGroup">
              <Select
                inline
                label="Property Type"
                options={PropertyTypeOptions}
                statekey="property_type"
              />
            </div>

            <div className="InputGroup">
              <Select
                inline
                label="Bedrooms"
                options={PropertyBedOptions}
                statekey="bedrooms"
              />
            </div>

            <div className="InputGroup">
              <Select
                inline
                label="Bathrooms"
                options={PropertyBedOptions}
                statekey="bathrooms"
              />
            </div>
            <div className="InputGroup">
              <Select
                inline
                label="Reception Rooms"
                options={PropertyBedOptions}
                statekey="reception_rooms"
              />
            </div>
            <div className="InputGroup">
              <Select
                inline
                label="Floor Area (sqm)"
                options={FloorAreaOptions}
                statekey="floor_area_sqm"
              />
            </div>
            <div className="InputGroup">
              <Select
                inline
                label="Year Built"
                options={YearBuiltOptions}
                statekey="year_built"
              />
            </div>
            <div className="InputGroup">
              <Select
                inline
                label="Property Tenure"
                options={PropertyFreeholdLeaseholdOptions}
                statekey="freehold_leasehold"
              />
            </div>
            <div className="InputGroup">
              <Select
                inline
                label="Council Tax Band"
                options={CouncilTaxOptions}
                statekey="council_tax_band"
              />
            </div>
            <div className="InputGroup">
              <Select
                inline
                label="EPC Rating"
                options={EpcOptions}
                statekey="epc_rating"
              />
            </div>
            <div className="InputGroup">
              <Select
                inline
                label="Primary Heating Source"
                options={PrimaryHeatingOptions}
                statekey="primary_heating"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default InfoStep;

InfoStep.propTypes = {};
